<template>
    <template v-for="(element, elementIndex) in (elements || [])" :key="`${uniqueKey}-element-${elementIndex}-${element.id || elementIndex}`">
        <template v-if="!isComponent(element) && !element.hide">
            <div
                v-if="isCardElement(element)"
                class="card shadow-sm border mb-3"
            >
                <div v-if="getCardTitle(element)" class="card-header px-3 py-2 border-bottom">
                    <h6 class="card-title mb-0">{{ getCardTitle(element) }}</h6>
                </div>
                <div class="card-body px-3 pt-3">
                    <div :class="getClass(element)">
                        <template v-if="hasChildren(element)">
                            <buildCustomFrom
                                :key="`${uniqueKey}-card-${elementIndex}-${element.id || elementIndex}`"
                                ref="buildCustomFrom"
                                :unique-key="`${uniqueKey}-card-${elementIndex}-${element.id || elementIndex}`"
                                :attributes="attributes"
                                :elements="element.children"
                                @changeValue="changeValueOnBuild"
                            />
                        </template>
                    </div>
                </div>
            </div>
            <div
                v-else
                :class="getClass(element)"
            >
                <template v-if="hasChildren(element)">
                    <buildCustomFrom
                        :key="`${uniqueKey}-div-${elementIndex}-${element.id || elementIndex}`"
                        ref="buildCustomFrom"
                        :unique-key="`${uniqueKey}-div-${elementIndex}-${element.id || elementIndex}`"
                        :attributes="attributes"
                        :elements="element.children"
                        @changeValue="changeValueOnBuild"
                    />
                </template>
            </div>
        </template>
        <template v-else>
            <component
                :key="`${uniqueKey}-component-${elementIndex}-${element.id || elementIndex}-${element.props?.name || ''}`"
                :is="getComponent(element)"
                v-model="element.modelValue"
                v-bind="element.props"
                v-on="componentEvents"
            />
        </template>
    </template>
</template>

<script>
import {toolbox} from "../../../../../../components/toolbox/toolbox.js";
import {markRaw} from "vue";
import {customFieldComponents} from "../../components/customFieldComponents.js";

export default {
    name: "buildCustomFrom",
    props: {
        attributes: {
            type: Object,
            required: true
        },
        elements: {
            type: Array,
            required: true
        },
        uniqueKey: {
            type: String,
            default: () => `build-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
        }
    },
    emits: [
        'changeValue'
    ],
    data() {
        return {
            componentEvents: {}
        }
    },
    methods: {
        hasChildren(element) {
            return (element.hasOwnProperty('children') && Array.isArray(element.children));
        },
        getCardTitle(element) {
            if(element.type === 'gridRow') {
                if(element.hasOwnProperty('rowLabel') && element.rowLabel) {
                    return element.rowLabel;
                }
            }
            return '';
        },
        getClass(element) {
            let newClass = [];
            if(element.type === 'gridRow') {
                newClass.push(element.class);
                newClass.push('g-2');
                if(!this.isCardElement(element)){
                    newClass.push('mb-2');
                }
                if(element.hasOwnProperty('rowClass') && Array.isArray(element.rowClass) && element.rowClass.length > 0) {
                    if(this.isCardElement(element)){
                        const newRowClass = [];
                        element.rowClass.forEach((className) => {
                            if(className !== 'card') {
                                newRowClass.push(className);
                            }
                        });
                        newClass = [...newClass, ...newRowClass];
                    } else {
                        newClass = [...newClass, ...element.rowClass];
                    }
                }
            } else if(element.type === 'gridColum') {
                newClass = [...element.class, ...element.columnClass];
            }
            return newClass;
        },
        isCardElement(element) {
            if(element.type === 'gridRow') {
                if(element.hasOwnProperty('rowClass') && Array.isArray(element.rowClass) && element.rowClass.length > 0) {
                    return element.rowClass.includes('card');
                }
            }
            return false;
        },
        isComponent(element) {
            return !['gridRow', 'gridColum'].includes(element.type);
        },
        getComponent(element) {
            if(toolbox.hasOwnProperty(element.type) || customFieldComponents.hasOwnProperty(element.type)) {
                this.componentEvents = {
                    'update:modelValue': (value) => {
                        if(this.attributes.hasOwnProperty(element.props.name)) {
                            this.$nextTick(() => {
                                this.attributes[element.props.name] = value;
                                this.$emit('changeValue', {
                                    fieldName: element.props.name,
                                    value: value
                                });
                            });
                        }
                    }
                };

                if(toolbox.hasOwnProperty(element.type)) {
                    element.modelValue = this.attributes[element.props.name] || null;
                    if(element.hasOwnProperty('propEvents')) {
                        Object.entries(element.propEvents).forEach(([eventName, func]) => {
                            element.props[eventName] = (event) => {
                                const dynamicFunction = new Function('event', 'context', 'attributes', `
                                    with(context) {
                                        return (function() {
                                            ${func}
                                        })();
                                    }
                                `);
                                return dynamicFunction.call(this, event, this, this.attributes);
                            };
                        });
                    }

                    if(element.hasOwnProperty('events')) {
                        Object.entries(element.events).forEach(([eventName, func]) => {
                            this.componentEvents[eventName] = (event) => {
                                const dynamicFunction = new Function('event', 'context', 'attributes', `with(context) { ${func} }`);
                                dynamicFunction.call(this, event, this, this.attributes);
                            };
                        });
                    }
                }

                if(toolbox.hasOwnProperty(element.type)) {
                    return markRaw(toolbox[element.type]);
                } else if(customFieldComponents.hasOwnProperty(element.type)) {
                    if(element.hasOwnProperty('props')) {
                        element.props['isCustomFrom'] = true;
                    } else {
                        element.props = {
                            isCustomFrom: true
                        };
                    }
                    return markRaw(customFieldComponents[element.type]);
                }
            }
            return null;
        },
        setModelValue(name, value) {
            if(this.attributes.hasOwnProperty(name)) {
                this.attributes[name] = value;
            }
        },
        changeValueOnBuild(event) {
            this.$emit('changeValue', event);
        }
    }
}
</script>

<style scoped>

</style>
